import React, { useContext, useState } from 'react';
import { BarcodeExportConfigurationContext } from '../../../../context/BarcodeExportConfigurationContext';
import { BarcodeExportConfigurationRequest } from '../../../../requests/barcodeExportConfiguration.request';
import { useRequest } from '../../../../requests/common/requestHook';
import {
  BarcodeExportConfiguration,
  BarcodeExportEntityType,
  barcodeExportEntityTypeToConfigurationEntityTypeMap,
} from '../../../../types/barcode_export_configuration/barcodeExportConfiguration';
import { TranslationKey } from '../../../../types/translation/TranslationKey';
import { translate } from '../../../../types/translation/Translator';
import { ExportUtil } from '../../../../util/export.util';
import { Dropdown, DropdownList } from '../../Dropdown';
import { Button, ButtonVariant } from '../Button';

interface GenerateBarcodeButtonProps {
  entityIds: string[];
  entityType: BarcodeExportEntityType;
  variant?: ButtonVariant;
  text?: string;
}

export default function GenerateBarcodeButton({ entityIds, entityType, variant, text }: GenerateBarcodeButtonProps) {
  const { barcodeExportConfigurations } = useContext(BarcodeExportConfigurationContext);

  if (!entityIds.length) return null;

  if (barcodeExportConfigurations.size === 0) return null;
  const configurations = [...barcodeExportConfigurations.values()];
  const applicableConfigurations = configurations.filter(
    config => config.entityType === barcodeExportEntityTypeToConfigurationEntityTypeMap[entityType],
  );
  if (applicableConfigurations.length === 0) return null;

  const [generate, { loading }] = useRequest(BarcodeExportConfigurationRequest.generate);

  const [open, setOpen] = useState(false);

  const handleGenerate = async (configuration: BarcodeExportConfiguration) => {
    const response = await generate({ configurationId: configuration.id, entityIds, entityType });
    if (!response) return;
    setOpen(false);
    ExportUtil.downloadExport(response);
  };

  return (
    <Dropdown
      open={open}
      setOpen={open => {
        if (!open) setOpen(false);
      }}
      dropdown={
        <DropdownList
          items={applicableConfigurations}
          textBuilder={item => item.name}
          onClick={item => handleGenerate(item)}
        />
      }
    >
      <Button
        text={text || translate({ key: TranslationKey.generateBarcode })}
        variant={variant}
        loading={loading}
        onClick={async () => {
          if (applicableConfigurations.length === 1) {
            return await handleGenerate(applicableConfigurations[0]);
          }
          setOpen(!open);
        }}
      />
    </Dropdown>
  );
}
